import {
  CoreQuery,
  EncryptBody,
  type Encrypted,
  type IClientResponse,
} from "@gen/app-query";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import { useMemo } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

import { ActionDialog } from "~/components/action_dialog";
import { entries, fromEntries, keys } from "~/utilities/object";

type ClientExportDialogProps = {
  open: boolean;
  onClose: () => void;
  clientData: IClientResponse;
  clientId: string;
};

type FormValues = {
  [K in keyof IClientResponse]: boolean;
};

export function ClientExportDialog({
  open,
  onClose,
  clientData,
  clientId,
}: ClientExportDialogProps) {
  const fields = useMemo(() => keys(clientData), [clientData]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: fields.reduce(
      (acc, f) => ({ ...acc, [f]: false }),
      {} as FormValues,
    ),
    shouldUnregister: false,
  });

  const { mutate: encrypt, isPending } = CoreQuery.useAdmin_encryptMutation({
    onSuccess(resp: Encrypted) {
      const blob = new Blob([resp.ciphertext], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${clientId}.enc`;
      a.click();
      URL.revokeObjectURL(url);
      onClose();
    },
    meta: { success: "Configuration exported" },
  });

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    const payload = fromEntries(
      entries(values)
        .filter(([_, v]) => v)
        .map(([k]) => {
          return [k, clientData[k]];
        }),
    ) as Partial<IClientResponse>;
    const plaintext = JSON.stringify(payload, null, 2);
    encrypt(EncryptBody.fromJS({ plaintext }));
  };

  return (
    <>
      {open && (
        <ActionDialog
          heading="Export Client Configuration"
          confirm={handleSubmit(onSubmit)}
          cancel={onClose}
          cancelButtonMessage="Close"
          confirmButtonMessage="Export"
          isConfirming={isPending}
          isDisabled={
            isSubmitting || isPending || !fields.some((f) => watch(f))
          }
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={1}>
              {fields.map((f) => (
                <Controller
                  key={f}
                  name={f}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          slotProps={{
                            input: {
                              "data-testid": `export-${f}-checkbox`,
                            },
                          }}
                        />
                      }
                      label={f}
                    />
                  )}
                />
              ))}
            </Stack>
          </form>
        </ActionDialog>
      )}
    </>
  );
}
